'use client';

import { TelegramProductShortResponseDto } from '@/generated';
import dynamic from 'next/dynamic';
import { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import { ServerFeed } from './server';

const hasRequestIdleCallback = () =>
  typeof window !== 'undefined' && typeof (window as any).requestIdleCallback === 'function';

type Props = {
  products: TelegramProductShortResponseDto[];
  isMobileDevice: boolean;
};

export function DeferredClientFeed({ products, isMobileDevice }: Props) {
  const [shouldMountInteractiveFeed, setShouldMountInteractiveFeed] = useState(false);
  const mountAnchorRef = useRef<HTMLDivElement | null>(null);

  const InteractiveFeed = useMemo(
    () =>
      dynamic(() => import('./client').then((mod) => mod.ClientFeed), {
        ssr: false,
        loading: () => <ServerFeed products={products} isMobileDevice={isMobileDevice} />,
      }),
    [products, isMobileDevice],
  );

  useEffect(() => {
    if (shouldMountInteractiveFeed) {
      return;
    }

    let cancelled = false;
    const mountInteractiveFeed = () => {
      if (!cancelled) {
        startTransition(() => {
          setShouldMountInteractiveFeed(true);
        });
      }
    };

    const idleId = hasRequestIdleCallback()
      ? (window as any).requestIdleCallback(mountInteractiveFeed, { timeout: 2000 })
      : window.setTimeout(mountInteractiveFeed, 1000);

    let observer: IntersectionObserver | null = null;
    const anchor = mountAnchorRef.current;
    if (anchor && typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            mountInteractiveFeed();
            observer?.disconnect();
          }
        },
        { rootMargin: '360px 0px' },
      );
      observer.observe(anchor);
    }

    return () => {
      cancelled = true;

      if (hasRequestIdleCallback()) {
        (window as any).cancelIdleCallback?.(idleId);
      } else {
        window.clearTimeout(idleId);
      }

      observer?.disconnect();
    };
  }, [shouldMountInteractiveFeed]);

  return (
    <div className='flex flex-col items-center' ref={mountAnchorRef}>
      {shouldMountInteractiveFeed ? (
        <InteractiveFeed products={products} isMobileDevice={isMobileDevice} />
      ) : (
        <ServerFeed products={products} isMobileDevice={isMobileDevice} />
      )}
    </div>
  );
}
